import { useEffect, useState, useCallback } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'site-theme';

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
    // Legacy admin-theme key migration
    const legacy = window.localStorage.getItem('admin-theme');
    if (legacy === 'dark' || legacy === 'light') return legacy as Theme;
  } catch {
    /* ignore */
  }
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  root.style.colorScheme = theme;
}

let listeners: Array<(t: Theme) => void> = [];
let currentTheme: Theme | null = null;

function setGlobalTheme(theme: Theme) {
  currentTheme = theme;
  applyTheme(theme);
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore */
  }
  listeners.forEach((l) => l(theme));
}

/**
 * Site-wide theme hook. Persists the user's choice across sessions and
 * keeps every component subscribed to the same value.
 */
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => currentTheme ?? getInitialTheme());

  useEffect(() => {
    if (currentTheme === null) {
      currentTheme = theme;
      applyTheme(theme);
    }
    const listener = (t: Theme) => setThemeState(t);
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  const setTheme = useCallback((next: Theme) => setGlobalTheme(next), []);
  const toggleTheme = useCallback(
    () => setGlobalTheme((currentTheme ?? 'light') === 'dark' ? 'light' : 'dark'),
    [],
  );

  return { theme, setTheme, toggleTheme, isDark: theme === 'dark' };
}
